import React, { useState, useEffect, useCallback } from 'react';
import { FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import { ColorPalette } from 'theme/colors';

import AppBar from 'components/AppBar';
import Loader from 'components/Loader';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import useIsMobile from 'hooks/useIsMobile';
import useToast from 'hooks/useToast';
import { getTravelStatus } from 'services/paymentMethodService';

const Container = styled.div`
  margin: 16px;
  background-color: ${ColorPalette.HEX.White};
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 6px 10px 0 rgba(${ColorPalette.RGB.DeepTaupe2}, 0.16);

  & li {
    font-size: 14px;
    color: ${ColorPalette.HEX.OuterSpace};
    line-height: 1.2em;
    display: flex;
    flex-direction: column;
    margin-bottom: 4px;

    & strong {
      font-weight: 600;
      color: ${ColorPalette.HEX.PineTree};
      font-size: 12px;
      margin-right: 5px;
    }
  }

  & textarea {
    font-family: 'Lucida Console', Monaco, monospace !important;
    font-size: 11px;
    width: 100%;
    height: 100px;
    color: #666;
    padding: 4px;
    border-color: #ccc;
    background: ${ColorPalette.HEX.Lotion};
    margin: 4px 0;
  }
`;

const Icon = styled.div``;

const CheckIcon = styled(FaCheckCircle)`
  color: #13b84e !important;
  width: 16px;
  height: 16px;
  vertical-align: bottom;
`;
const ErrorIcon = styled(FaExclamationCircle)`
  color: ${ColorPalette.HEX.RedRYB} !important;
  width: 16px;
  height: 16px;
  vertical-align: bottom;
`;

interface Params {
  match: {
    params: {
      travelId?: string;
    };
  };
}

interface PaymentDetail {
  accountPaymentId: number;
  amount: number;
  companyId: string;
  createdAtUTC: string;
  currency: string;
  id: string;
  operation: string;
  optimisticPayment: boolean;
  orderId: number;
  paymentExecutedSuccessfully: boolean;
  travelId: string;
  userId: string;
  errorDescription?: string;
}

const PaymentTravelStatus = ({ match: { params } }: Params) => {
  const [isLoading, setIsLoading] = useState(!!params.travelId);
  const toast = useToast();
  const [list, setList] = useState<PaymentDetail[]>();
  const isMobile = useIsMobile();
  const history = useHistory();

  const handleGetData = useCallback(async () => {
    setIsLoading(true);
    if (typeof params.travelId !== 'undefined') {
      try {
        const { data } = await getTravelStatus(params.travelId);
        setIsLoading(false);
        setList(data);
      } catch (error) {
        toast.add({
          type: 'danger',
          message: error.message,
        });
        setIsLoading(false);
      }
    }
  }, [params.travelId, toast]);

  useEffect(() => {
    handleGetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Mobile>
        <AppBar goBack={() => history.goBack()}>
          <h5>Payment Status</h5>
        </AppBar>
      </Mobile>

      {isLoading ? (
        <Loader fullScreen={isMobile} />
      ) : (
        <>
          <Desktop>
            <ModalHeader title="Payment Status" onClose={() => history.goBack()} />
          </Desktop>
          {typeof list === 'undefined' ? (
            <Container>List is undefined</Container>
          ) : list.length === 0 ? (
            <Container>No entries found</Container>
          ) : (
            list.map((item: PaymentDetail, key) => (
              <Container key={key}>
                <ul>
                  <li>
                    <strong>accountPaymentId:</strong>
                    {item.accountPaymentId}
                  </li>
                  <li>
                    <strong>amount:</strong>
                    {item.amount}
                  </li>
                  <li>
                    <strong>companyId:</strong>
                    {item.companyId}
                  </li>
                  <li>
                    <strong>createdAtUTC:</strong>
                    {item.createdAtUTC}
                  </li>
                  <li>
                    <strong>currency:</strong>
                    {item.currency}
                  </li>
                  <li>
                    <strong>id:</strong>
                    {item.id}
                  </li>
                  <li>
                    <strong>operation:</strong>
                    {item.operation}
                  </li>
                  <li>
                    <strong>optimisticPayment:</strong>
                    {item.optimisticPayment ? 'true' : 'false'}
                  </li>
                  <li>
                    <strong>orderId:</strong>
                    {item.orderId}
                  </li>
                  <li>
                    <strong>paymentExecutedSuccessfully:</strong>
                    {item.paymentExecutedSuccessfully ? (
                      <Icon>
                        <CheckIcon /> true
                      </Icon>
                    ) : (
                      <Icon>
                        <ErrorIcon /> false
                      </Icon>
                    )}
                  </li>
                  <li>
                    <strong>travelId:</strong>
                    {item.travelId}
                  </li>
                  <li>
                    <strong>userId:</strong>
                    {item.userId}
                  </li>
                  {item.errorDescription ? (
                    <li>
                      <strong>errorDescription:</strong>
                      <textarea>{JSON.stringify(JSON.parse(item.errorDescription), null, 2)}</textarea>
                    </li>
                  ) : null}
                </ul>
              </Container>
            ))
          )}
        </>
      )}
    </>
  );
};

export default PaymentTravelStatus;
